import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { PayoutsApiAdapter } from '../../../Payouts/List/adapters/PayoutsApiAdapter';
import type { SimpleFilterOption } from '@shared/components/ui/SimpleFilter';

interface PayoutsInfiniteResponse {
  data: Array<{
    id: number;
    payout_date: string;
  }>;
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

/**
 * Hook para obtener payouts con scroll infinito
 * Usa useInfiniteQuery para cargar páginas de 25 items
 */
export const usePayoutsInfinite = () => {
  const httpClient = createHttpClient(apiClientWH);
  const payoutsAdapter = new PayoutsApiAdapter(httpClient);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<PayoutsInfiniteResponse, Error>({
    queryKey: ['payouts-infinite'],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await payoutsAdapter.getPayouts({
        per_page: 25,
        page: pageParam as number,
        // No enviar sorting sin column - la API exige sorting.column cuando sorting está presente
      });

      return {
        data: response.data,
        meta: response.meta,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.current_page < lastPage.meta.last_page) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos en cache
  });

  // 🎯 Función de búsqueda para DynamicCombobox
  // Filtra los payouts cargados localmente
  const searchFn = useCallback(
    async (query: string): Promise<SimpleFilterOption[]> => {
      const allPayouts = data?.pages.flatMap((page) => page.data) || [];

      // Si hay query, filtrar localmente
      if (query.trim()) {
        const filtered = allPayouts.filter((payout) =>
          `#${payout.id} - ${payout.payout_date}`
            .toLowerCase()
            .includes(query.toLowerCase())
        );

        return filtered.map((payout) => ({
          label: `#${payout.id} - ${payout.payout_date}`,
          value: String(payout.id),
        }));
      }

      // Sin query, devolver todos los payouts cargados
      return allPayouts.map((payout) => ({
        label: `#${payout.id} - ${payout.payout_date}`,
        value: String(payout.id),
      }));
    },
    [data]
  );

  // 🎯 Handler para cargar más páginas (scroll infinito)
  const handleLoadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    searchFn,
    isLoading,
    error: error?.message || null,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    onLoadMore: handleLoadMore,
  };
};

